// ---------------------------------------------------------------------------
// Tappable links in the chat transcript. Lenny's replies are plain text, so an
// article URL he mentioned used to show up as dead text (Robbie, 2026-09-30:
// "When it mentions a link in the dialogue, its not clickable").
//
// splitLinks() cuts a message into text and link segments; VoiceAgent renders
// the links as <a> elements. Nothing goes through innerHTML and only http(s)
// URLs ever become an href. It recognises:
//   - full URLs:            https://www.foodfacts.org/articles/oat-milk
//   - bare site paths:      foodfacts.org/articles/oat-milk (the brand name on
//                           its own, as in "Lenny from FoodFacts.org", stays text)
//   - markdown links:       [Oat milk](https://www.foodfacts.org/articles/oat-milk)
//                           (the prompt forbids markdown, but it slips through)
// ---------------------------------------------------------------------------

// A URL runs until whitespace, a quote or bracket that can't be part of it, or
// CJK / Devanagari punctuation (Lenny also answers in Chinese and Hindi).
const LINK_RE = /\[([^\]\n]{1,200})\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"'`‘’“”…।　-〿＀-￯]+|\b(?:www\.)?foodfacts\.org\/[^\s<>"'`‘’“”…।　-〿＀-￯]*)/gi;

const CLOSERS = { ')': '(', ']': '[', '}': '{' };

// Sentence punctuation after a URL ("...oat-milk." or "(see https://x.org/a)")
// is not part of it; a closing bracket stays when the URL itself opened one.
function trimUrl(url) {
  let end = url.length;
  while (end > 0) {
    const ch = url[end - 1];
    if ('.,;:!?*'.includes(ch)) { end -= 1; continue; }
    if (CLOSERS[ch]) {
      const body = url.slice(0, end);
      if (body.split(ch).length > body.split(CLOSERS[ch]).length) { end -= 1; continue; }
    }
    break;
  }
  return url.slice(0, end);
}

function toHref(raw) {
  const url = /^https?:\/\//i.test(raw) ? raw : raw.replace(/^(?:www\.)?foodfacts\.org/i, 'https://www.foodfacts.org');
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch (e) {
    return null;
  }
}

// Returns [{ type: 'text', value }, { type: 'link', href, label, internal }, ...].
export function splitLinks(text) {
  const parts = [];
  if (!text) return parts;
  const pushText = (value) => { if (value) parts.push({ type: 'text', value }); };
  let last = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    let raw, label, end;
    if (m[2]) {
      raw = m[2];
      label = m[1];
      end = LINK_RE.lastIndex;
    } else {
      raw = trimUrl(m[3]);
      label = raw.replace(/^(https?:\/\/)?(www\.)?/i, ''); // shorter on a phone; the href keeps the full URL
      end = m.index + raw.length;
      LINK_RE.lastIndex = Math.max(end, m.index + 1); // re-read the trimmed punctuation as text
    }
    const href = toHref(raw);
    if (!href) continue;
    pushText(text.slice(last, m.index));
    parts.push({ type: 'link', href, label, internal: /^https?:\/\/(www\.)?foodfacts\.org(\/|$)/.test(href) });
    last = end;
  }
  pushText(text.slice(last));
  return parts;
}
